import React, { useCallback } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import EditModalFooter from './EditModalFooter'
import { PatchNoteItemViewModel } from './models'
import {
  extractEditableStartDateTimeStrings,
  updateNotePayloadFromViewModel,
} from './utils'
import { formOptions } from '../Common/Fields/config'
import NoteFields from '../Common/NoteFields'
import DeleteTripItem from '../DeleteTripItem'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { usePatchTripItem } from 'tripPlanner/hooks/api/tripItem'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { NoteItem } from 'tripPlanner/types/tripItem'

interface Props {
  item: NoteItem
}

const itemToView = (item: NoteItem): PatchNoteItemViewModel => {
  return {
    type: item.type,
    title: item.title,
    link: null,
    notes: item.notes,
    ...extractEditableStartDateTimeStrings(item),
    startTime: null,
    endDate: null,
    endTime: null,
    isSectionHeader: item.isSectionHeader,
  }
}

function EditNote(props: Props) {
  const { item } = props

  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const methods = useForm<PatchNoteItemViewModel>({
    ...formOptions,
    defaultValues: itemToView(item),
  })

  const { handleSubmit } = methods

  const { mutate: updateMutate, isLoading } = usePatchTripItem({
    onSuccess: () => {
      closeModal()
    },
  })

  const deleteItem = useCallback(
    () => openModal(DeleteTripItem, { item }, { height: 'auto' }),
    [openModal, item],
  )

  const updateItem = useCallback(
    (viewModel: PatchNoteItemViewModel) => {
      updateMutate({
        tripId: item.tripId,
        tripItemId: item.id,
        tripItem: updateNotePayloadFromViewModel(viewModel),
      })
    },
    [item, updateMutate],
  )

  return (
    <>
      <ModalHeader title="Edit Note" onCloseButtonClick={closeModal} />
      <ModalBody>
        <FormProvider {...methods}>
          <ModalContent>
            <NoteFields tripId={item.tripId} />
          </ModalContent>
        </FormProvider>
      </ModalBody>
      <EditModalFooter
        deleteItem={deleteItem}
        onSubmit={handleSubmit(updateItem)}
        isLoading={isLoading}
      />
      {isLoading && <LoadingIndicator floating />}
    </>
  )
}

export default EditNote
